import { cloneDeep, get, isEmpty, isNil, set } from "lodash";
import moment from "moment";
import { RELATIONSHIP } from "utils/constants";
import { evaluateConditions } from "utils/validation-rules";
import { currentSubscriptionOf } from "./utils";

export { evaluateConditions };

export const CUSTOM_ENTITY_KEY = "custom";

export const storageKeyOf = (binding, fieldDefinition) => {
  // Mirrors the API: custom.<key> always means project.fields.<key>. Deriving
  // it here too keeps the pending key stable while the registry is still
  // loading, so it cannot briefly disagree with the persisted one.
  // advanced.* canvas bindings write the same project.fields bag.
  if (binding.entity === CUSTOM_ENTITY_KEY || binding.entity === "advanced")
    return `project.fields.${binding.field}`;
  const target = get(fieldDefinition, "target");
  if (!target) {
    // Registry may still be loading; keep entity.field so state conditions
    // (e.g. user.age from user.birthDate) can resolve pending values.
    return binding.entity
      ? `${binding.entity}.${binding.field}`
      : binding.field;
  }
  return target.filterValue
    ? `${target.entity}.${target.property}.${target.filterValue}`
    : `${target.entity}.${target.property}`;
};

const PROJECT_FIELDS_PREFIX = "project.fields.";
const FIELDS_PREFIX = "fields.";
const CUSTOM_FIELDS_PREFIX = `${CUSTOM_ENTITY_KEY}.`;
const ADVANCED_FIELDS_PREFIX = "advanced.";

/**
 * Storage keys ("project.fields.X") and form accessors ("custom.X", "fields.X")
 * name the same project field, so every shape normalizes onto "fields.X" before
 * the two sides are compared.
 */
export const normalizeFieldKey = (key) => {
  if (typeof key !== "string") return key;
  if (key.indexOf(PROJECT_FIELDS_PREFIX) === 0)
    return `${FIELDS_PREFIX}${key.slice(PROJECT_FIELDS_PREFIX.length)}`;
  if (key.indexOf(CUSTOM_FIELDS_PREFIX) === 0)
    return `${FIELDS_PREFIX}${key.slice(CUSTOM_FIELDS_PREFIX.length)}`;
  if (key.indexOf(ADVANCED_FIELDS_PREFIX) === 0)
    return `${FIELDS_PREFIX}${key.slice(ADVANCED_FIELDS_PREFIX.length)}`;
  return key;
};

const rememberBufferedField = (buffered, fieldKey, value) => {
  buffered[fieldKey] = value;
  // `fields.needs.<category>` is read as a path. The flat storage key must
  // stay. Pass the path as parts: lodash treats a dotted string as one key
  // once that flat key exists, and would not build the nest.
  if (fieldKey.indexOf("needs.") === 0)
    set(buffered, fieldKey.split("."), value);
};

const applyPendingField = (context, buffered, key, value) => {
  // storageKeyOf may return "project.fields.X" while state conditions use
  // "fields.X" — normalize both shapes onto the project field bag.
  if (key.indexOf(PROJECT_FIELDS_PREFIX) === 0) {
    const fieldKey = key.slice(PROJECT_FIELDS_PREFIX.length);
    rememberBufferedField(buffered, fieldKey, value);
    set(context, `${FIELDS_PREFIX}${fieldKey}`, value);
    return;
  }
  if (key.indexOf(FIELDS_PREFIX) === 0) {
    const fieldKey = key.slice(FIELDS_PREFIX.length);
    rememberBufferedField(buffered, fieldKey, value);
    set(context, key, value);
    return;
  }
  if (key.indexOf(ADVANCED_FIELDS_PREFIX) === 0) {
    const fieldKey = key.slice(ADVANCED_FIELDS_PREFIX.length);
    rememberBufferedField(buffered, fieldKey, value);
    set(context, `${FIELDS_PREFIX}${fieldKey}`, value);
    set(context, key, value);
    return;
  }
  if (key.indexOf(".") < 0) {
    buffered[key] = value;
    return;
  }
  set(context, key, value);
  // Entity keys ("user.lastname", "contract.issuanceDate",
  // "telecom.value.EMAIL") must also live in the fields bag so remounts
  // (accordion Modifier) can restore typed values via resolveEntityValue.
  buffered[key] = value;
};

const deriveUserAge = (context) => {
  // Pending edits land on user.birthDate; persisted values live under contact.
  const birthDate =
    get(context, "user.birthDate") || get(context, "contact.user.birthDate");
  if (!birthDate) return undefined;
  const birth = moment(birthDate);
  if (!birth.isValid()) return undefined;
  return moment().diff(birth, "years");
};

const ANONYMOUS_REQUESTER = { type: null };

const spouseOf = (context) =>
  (get(context, "contact.relatedPersons") || []).find(
    (person) => get(person, "relationship") === RELATIONSHIP.SPOUSE,
  );

export const buildStateContext = (project, pendingFields, requester) => {
  const context = cloneDeep(project || {});
  const spouse = spouseOf(context);
  if (!isNil(spouse) && isNil(context.spouse)) {
    context.spouse = cloneDeep(spouse);
  }
  const subscription = currentSubscriptionOf(context);
  if (isNil(context.payment) && !isNil(get(subscription, "payment"))) {
    context.payment = cloneDeep(subscription.payment);
  }
  const buffered = {};
  // Persisted fields use the same flat keys as pending ones
  // ("project.fields.X") and must share the same normalization path.
  for (const [key, value] of Object.entries(get(context, "fields") || {})) {
    applyPendingField(context, buffered, key, value);
  }
  for (const [key, value] of Object.entries(pendingFields || {})) {
    applyPendingField(context, buffered, key, value);
  }
  context.fields = buffered;
  const age = deriveUserAge(context);
  if (age !== undefined) {
    set(context, "user.age", age);
  }
  // Set last so a project value can never impersonate the token's requester.
  context.requester = { ...ANONYMOUS_REQUESTER, ...(requester || {}) };
  return context;
};

const collectStampedSlotIds = (htmlContent) => {
  const stamped = new Set();
  if (!htmlContent) return stamped;
  const pattern = /data-kayros-slot=["']([^"']+)["']/g;
  let match = pattern.exec(htmlContent);
  while (match) {
    stamped.add(match[1]);
    match = pattern.exec(htmlContent);
  }
  return stamped;
};

/**
 * Prefer the active manifest; when empty (e.g. after a draft copy), fall back
 * only to a state whose slots match the HTML, or the sole state with slots.
 */
export const resolveSlots = (manifest, states = [], htmlContent = "") => {
  const primary = Array.isArray(manifest?.slots) ? manifest.slots : [];
  if (primary.length) return primary;

  const statesWithSlots = (states || []).filter(
    (state) =>
      Array.isArray(get(state, "bindingManifest.slots")) &&
      state.bindingManifest.slots.length,
  );
  if (!statesWithSlots.length) return [];
  if (statesWithSlots.length === 1) {
    return statesWithSlots[0].bindingManifest.slots;
  }

  const stamped = collectStampedSlotIds(htmlContent);
  if (!stamped.size) return [];

  let bestSlots = null;
  let bestScore = 0;
  for (const state of statesWithSlots) {
    const slots = state.bindingManifest.slots;
    let score = 0;
    for (const slot of slots) {
      if (stamped.has(slot.id)) score += 1;
    }
    if (score > bestScore) {
      bestScore = score;
      bestSlots = slots;
    }
  }
  return bestScore > 0 ? bestSlots : [];
};

export const resolveActiveState = (states, context) => {
  if (isEmpty(states)) return null;
  const ordered = [...states].sort((a, b) => a.order - b.order);
  return (
    ordered.find((state) => evaluateConditions(state.conditions, context)) ||
    null
  );
};
